import { AuthToken } from '../../api/types';

const AUTH_TOKEN_STORAGE_KEY = 'app_seller_admin_auth_token';

export function saveAuthToken(authToken: AuthToken): void {
  const serializedToken = JSON.stringify(authToken);
  localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, serializedToken);
  sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
}

export function readAuthToken(): AuthToken | null {
  const sharedValue = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  const legacyValue = sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  const rawValue = sharedValue ?? legacyValue;

  if (rawValue === null) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as unknown;

    if (!isAuthToken(parsedValue)) {
      clearAuthToken();

      return null;
    }

    const expiresAt = Date.parse(parsedValue.expires_at);

    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      clearAuthToken();

      return null;
    }

    if (legacyValue !== null) {
      saveAuthToken(parsedValue);
    }

    return parsedValue;
  } catch {
    clearAuthToken();

    return null;
  }
}

export function clearAuthToken(): void {
  sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
}

export function subscribeToAuthTokenChanges(onChange: (token: AuthToken | null) => void): () => void {
  const handleStorage = (event: StorageEvent): void => {
    const isAuthChange = event.key === AUTH_TOKEN_STORAGE_KEY || event.key === null;

    if (event.storageArea !== localStorage || !isAuthChange) {
      return;
    }

    sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    const token = readAuthToken();
    onChange(token);
  };

  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
}

function isAuthToken(value: unknown): value is AuthToken {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<AuthToken>;

  return (
    typeof candidate.access_token === 'string' &&
    candidate.token_type === 'Bearer' &&
    typeof candidate.expires_at === 'string' &&
    typeof candidate.user === 'object' &&
    candidate.user !== null &&
    typeof candidate.user.user_id === 'number' &&
    typeof candidate.user.user_email === 'string' &&
    typeof candidate.user.user_status_id === 'number' &&
    typeof candidate.user.user_email_confirmed === 'boolean'
  );
}
